import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import type { OrganizationCard as LegacyCardData } from "@/features/members/members.types";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { cardHover, stagger } from "@/lib/cards";
import { LegacyMark } from "@/components/LegacyMark";

/**
 * A legacy, as the directory shows it — and as the admin queue shows one
 * waiting for review, with the decision underneath (`footer`). Name, location,
 * stewards, then mediums: the description and the website belong on the page
 * the card opens, not repeated in a grid of cards. The whole card opens that
 * page; each steward is a button to theirs. The name's link stretches an
 * overlay across the card and the buttons lift above it, since anchors cannot
 * nest.
 *
 * The people come directly under the name and the mediums sit at the bottom,
 * matching MemberCard, where the legacies a steward manages are what you read
 * next after who they are. The two cards are read side by side in the
 * directory, so the same thing has to be in the same place on both.
 */
export function LegacyCard({
  legacy,
  index = 0,
  footer,
}: {
  legacy: LegacyCardData;
  index?: number;
  footer?: ReactNode;
}) {
  return (
    <Card
      className={`group animate-item-enter relative ${cardHover}`}
      style={stagger(index)}
    >
      <CardContent className="flex flex-1 gap-3">
        <LegacyMark imageUrl={legacy.image_url} name={legacy.name} />
        <div className="flex min-w-0 flex-1 flex-col">
          <Link
            to={`/organizations/${legacy.id}`}
            className="font-medium underline-offset-4 after:absolute after:inset-0 group-hover:underline"
          >
            {legacy.name}
          </Link>
          {legacy.location && (
            <p className="text-muted-foreground truncate text-xs">{legacy.location}</p>
          )}
          {legacy.members.length > 0 && (
            <div className="relative z-10 mt-2 flex flex-wrap gap-1.5">
              {legacy.members.map((m) =>
                m.redacted ? (
                  <span
                    key={m.id}
                    aria-label="Steward available to full members"
                    className="bg-muted-foreground/25 inline-block h-7 w-24 rounded-md align-middle"
                  />
                ) : (
                  <Button
                    key={m.id}
                    asChild
                    variant="outline"
                    size="sm"
                    className="h-auto min-h-7 max-w-full items-start py-1 text-left whitespace-normal"
                  >
                    <Link to={`/directory/${m.id}`}>
                      <Avatar aria-hidden="true" className="size-4">
                        <AvatarImage src={m.image_url ?? undefined} alt="" />
                        <AvatarFallback className="text-[10px]">{m.name.charAt(0)}</AvatarFallback>
                      </Avatar>
                      {m.name}
                    </Link>
                  </Button>
                )
              )}
            </div>
          )}
          {(legacy.mediums ?? []).length > 0 && (
            <div className="mt-auto flex flex-wrap gap-1 pt-2">
              {legacy.mediums.slice(0, 3).map((k) => (
                <Badge key={k} variant="secondary" className="text-xs">
                  {k}
                </Badge>
              ))}
            </div>
          )}
          {footer && <div className="relative z-10 mt-3">{footer}</div>}
        </div>
      </CardContent>
    </Card>
  );
}
